import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import TablePagination from './TablePagination';
import { DEFAULT_PAGE_SIZE, getPaginationMeta } from '../../utils/pagination';
import {
  TOOL_TYPE_LABELS,
  formatToolCreatedAt,
  loadMyTools,
} from '../../utils/billToFlowTools';

function AdminMyTools() {
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const tools = useMemo(() => loadMyTools(), [location.key]);
  const pageSize = DEFAULT_PAGE_SIZE;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tools;
    return tools.filter((tool) =>
      [tool.toolName, tool.toolCode, TOOL_TYPE_LABELS[tool.toolType] || tool.toolType, tool.createdBy]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [tools, search]);

  const { currentPage, totalCount } = getPaginationMeta(filtered.length, page, pageSize);
  const pageItems = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <DashboardLayout title="My Tools">
      <div className="space-y-4 py-4">
        <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full min-w-0 md:max-w-xs">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search My Tools"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full rounded-lg border border-gray-200 bg-[#faf8f5] py-2 pl-10 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
            <Link
              to="/dashboard/admin/bill-to-flow/create"
              className="inline-flex shrink-0 items-center justify-center rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm"
            >
              Create Projected Tool
            </Link>
          </div>

          {pageItems.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              {search.trim()
                ? 'No tools match your search.'
                : 'No tools created yet. Use Create Projected Tool to add one.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                      Sr. No.
                    </th>
                    <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                      Tool Name
                    </th>
                    <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                      Tool Code
                    </th>
                    <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                      Tool Type
                    </th>
                    <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                      Date
                    </th>
                    <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                      Created On
                    </th>
                    <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                      Created By
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((tool, index) => (
                    <tr key={tool.id || index} className="hover:bg-amber-50/40">
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 font-medium text-gray-900">
                        {tool.toolName || '—'}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {tool.toolCode || '—'}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {TOOL_TYPE_LABELS[tool.toolType] || tool.toolType || '—'}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {tool.toolDate || '—'}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {formatToolCreatedAt(tool.createdAt)}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {tool.createdBy || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <TablePagination
            totalCount={totalCount}
            currentPage={currentPage}
            onPageChange={setPage}
            pageSize={pageSize}
          />
        </div>
      </div>
    </DashboardLayout>
  );
}

export default AdminMyTools;
